/**
 * Per-model cost display helpers.
 *
 * Model rows arrive on the client with `pass_through` pricing whose `costSpec`
 * holds the RAW provider rate (USD, pre-markup). The user is billed
 * `raw × (1 + markupPercent/100)`, so everything shown to the user here is the
 * marked-up amount — it matches what the credit ledger will actually deduct.
 *
 * Units follow the platform convention (see `./format-credits`): 1 credit = 1 US
 * cent, displayed as credits with a dollar equivalent available in the tooltip.
 * Text rates are quoted per 1,000,000 tokens (the industry-standard unit); image
 * rates per image; audio rates per 1K characters / per second.
 */
import type { ArchePricing, ModelPassThroughPricing } from './api';
import { formatCredits, formatDollars } from './format-credits';

const CREDITS_PER_DOLLAR = 100;
const TOKENS_PER_MILLION = 1_000_000;
const CHARS_PER_UNIT = 1_000;

export type ModelPricingInput = ModelPassThroughPricing | ArchePricing | null | undefined;

export interface ModelCostInfo {
  kind: 'per_token' | 'per_call' | 'per_character' | 'per_second' | 'per_tier';
  /** User-facing (marked-up) credit amounts, keyed by their display unit. */
  credits: {
    inputPerMillionTokens?: number;
    outputPerMillionTokens?: number;
    perImage?: number;
    per1kCharacters?: number;
    perSecond?: number;
    /** Per-image credits at each tier (e.g. output resolution), for `per_tier`. */
    perTier?: Record<string, number>;
  };
  markupPercent: number;
  /** Compact one-line label suited to a select item / dropdown. */
  compact: string;
  /** Fuller multi-line breakdown suited to a tooltip. */
  detail: string;
}

/** RAW provider USD → user-facing (marked-up) credits. */
function usdToCredits(rawUsd: number, markupPercent: number): number {
  const credits = rawUsd * (1 + markupPercent / 100) * CREDITS_PER_DOLLAR;
  // Kill floating-point dust (e.g. 360.00000000000006) so whole-number credit
  // amounts render as "360" not "360.00". 6 dp sits well below the 4-dp display
  // resolution in format-credits, so this never changes a visible figure.
  return Math.round(credits * 1e6) / 1e6;
}

function isPassThrough(pricing: ModelPricingInput): pricing is ModelPassThroughPricing {
  return (
    !!pricing &&
    (pricing as ModelPassThroughPricing).model === 'pass_through' &&
    !!(pricing as ModelPassThroughPricing).costSpec
  );
}

/**
 * Per-quality price spans for tiers keyed `quality@WIDTHxHEIGHT` (an image
 * model priced by quality and size), cheapest quality first — or null when
 * the tiers are keyed any other way (e.g. by resolution).
 */
function groupQualityAndSizeTiers(
  perTier: Record<string, number>,
): { quality: string; min: number; max: number }[] | null {
  const spans = new Map<string, { quality: string; min: number; max: number }>();
  for (const [tier, credits] of Object.entries(perTier)) {
    const match = /^([a-z]+)@\d+x\d+$/.exec(tier);
    if (!match) return null;
    const span = spans.get(match[1]) ?? { quality: match[1], min: credits, max: credits };
    span.min = Math.min(span.min, credits);
    span.max = Math.max(span.max, credits);
    spans.set(match[1], span);
  }
  return spans.size > 0 ? [...spans.values()].sort((a, b) => a.min - b.min) : null;
}

function markupNote(markupPercent: number): string {
  return markupPercent > 0 ? `Includes ${markupPercent}% platform markup.` : '';
}

/**
 * Derive a display-ready cost descriptor from a model's pricing row, or `null`
 * when pricing is absent / not a priced pass-through row (free entities, or the
 * defensively-typed `ArchePricing` arm). Callers render nothing on `null`.
 */
export function getModelCostInfo(pricing: ModelPricingInput): ModelCostInfo | null {
  if (!isPassThrough(pricing)) return null;

  const markupPercent = pricing.markupPercent ?? 0;
  const spec = pricing.costSpec;

  switch (spec.kind) {
    case 'per_token': {
      const input = usdToCredits(spec.inputUsdPerToken * TOKENS_PER_MILLION, markupPercent);
      const output = usdToCredits(spec.outputUsdPerToken * TOKENS_PER_MILLION, markupPercent);
      const detailLines = [
        `Input: ${formatCredits(input)} credits (${formatDollars(input)}) per 1M tokens`,
        `Output: ${formatCredits(output)} credits (${formatDollars(output)}) per 1M tokens`,
      ];
      if (
        spec.highTierThresholdInputTokens &&
        spec.highTierInputUsdPerToken &&
        spec.highTierOutputUsdPerToken
      ) {
        const hiIn = usdToCredits(
          spec.highTierInputUsdPerToken * TOKENS_PER_MILLION,
          markupPercent,
        );
        const hiOut = usdToCredits(
          spec.highTierOutputUsdPerToken * TOKENS_PER_MILLION,
          markupPercent,
        );
        detailLines.push(
          `Above ${formatCredits(spec.highTierThresholdInputTokens / 1000)}K input tokens: ${formatCredits(hiIn)} in / ${formatCredits(hiOut)} out credits per 1M`,
        );
      }
      if (markupNote(markupPercent)) detailLines.push(markupNote(markupPercent));
      return {
        kind: 'per_token',
        credits: {
          inputPerMillionTokens: input,
          outputPerMillionTokens: output,
        },
        markupPercent,
        compact: `${formatCredits(input)}/${formatCredits(output)} cr per 1M tok`,
        detail: detailLines.join('\n'),
      };
    }
    case 'per_call': {
      const perImage = usdToCredits(spec.rawCostUsd, markupPercent);
      const detail = [
        `${formatCredits(perImage)} credits (${formatDollars(perImage)}) per image`,
        markupNote(markupPercent),
      ]
        .filter(Boolean)
        .join('\n');
      return {
        kind: 'per_call',
        credits: { perImage },
        markupPercent,
        compact: `~${formatCredits(perImage)} cr/image`,
        detail,
      };
    }
    case 'per_character': {
      const per1k = usdToCredits(spec.usdPerChar * CHARS_PER_UNIT, markupPercent);
      const detail = [
        `${formatCredits(per1k)} credits (${formatDollars(per1k)}) per 1K characters`,
        markupNote(markupPercent),
      ]
        .filter(Boolean)
        .join('\n');
      return {
        kind: 'per_character',
        credits: { per1kCharacters: per1k },
        markupPercent,
        compact: `~${formatCredits(per1k)} cr/1K chars`,
        detail,
      };
    }
    case 'per_second': {
      const perSecond = usdToCredits(spec.usdPerSecond, markupPercent);
      const detail = [
        `${formatCredits(perSecond)} credits (${formatDollars(perSecond)}) per second`,
        markupNote(markupPercent),
      ]
        .filter(Boolean)
        .join('\n');
      return {
        kind: 'per_second',
        credits: { perSecond },
        markupPercent,
        compact: `~${formatCredits(perSecond)} cr/sec`,
        detail,
      };
    }
    case 'per_tier': {
      // Priced by tier — for images, by output resolution — so the label is
      // a range, and the tooltip lists each tier. Metered tokens (reference
      // images, thinking) depend on the request and come on top.
      const perTier = Object.fromEntries(
        Object.entries(spec.rawCostUsdByTier).map(([tier, usd]) => [
          tier,
          usdToCredits(usd, markupPercent),
        ]),
      );
      const amounts = Object.values(perTier);
      const low = Math.min(...amounts);
      const high = Math.max(...amounts);
      const byQuality = groupQualityAndSizeTiers(perTier);
      const detail = [
        ...(byQuality
          ? // Priced by quality × pixel size (GPT Image): dozens of tiers, so
            // one line per quality, spanning its sizes.
            byQuality.map(({ quality, min, max }) =>
              min === max
                ? `${quality}: ${formatCredits(min)} credits (${formatDollars(min)}) per image`
                : `${quality}: ${formatCredits(min)}–${formatCredits(max)} credits (${formatDollars(min)}–${formatDollars(max)}) per image, by size`,
            )
          : Object.entries(perTier)
              .sort((a, b) => a[1] - b[1])
              .map(
                ([tier, credits]) =>
                  `${tier}: ${formatCredits(credits)} credits (${formatDollars(credits)}) per image`,
              )),
        spec.inputUsdPerToken !== undefined
          ? byQuality
            ? 'The prompt and reference images are billed as tokens on top.'
            : 'Reference images and thinking are billed as tokens on top.'
          : '',
        markupNote(markupPercent),
      ]
        .filter(Boolean)
        .join('\n');
      return {
        kind: 'per_tier',
        credits: { perTier },
        markupPercent,
        compact:
          low === high
            ? `~${formatCredits(low)} cr/image`
            : `~${formatCredits(low)}–${formatCredits(high)} cr/image`,
        detail,
      };
    }
    default: {
      // Exhaustiveness guard — a new costSpec kind must add a case above.
      const _never: never = spec;
      return _never;
    }
  }
}
